/**
 * usePwaInstall — Hook per catturare l'evento beforeinstallprompt e gestire
 * l'installazione della PWA.
 *
 * Chrome/Android emettono `beforeinstallprompt` quando tutti i requisiti
 * PWA sono soddisfatti (HTTPS, valid manifest, SW attivo). Il browser può
 * comunque decidere di NON emetterlo (es. utente ha già rifiutato).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface UsePwaInstallReturn {
  /** true se il browser ha emesso beforeinstallprompt (app è installabile) */
  isInstallable: boolean;
  /** true se l'app è già in modalità standalone (installata) */
  isInstalled: boolean;
  /** true se l'utente ha cliccato il bottone e il prompt è in corso */
  isPrompting: boolean;
  /** Avvia il prompt di installazione nativo del browser */
  promptInstall: () => Promise<boolean>;
}

export function usePwaInstall(): UsePwaInstallReturn {
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isPrompting, setIsPrompting] = useState(false);

  // ── Rileva se l'app è già installata (standalone mode) ──
  useEffect(() => {
    const checkStandalone = () => {
      const standalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        // @ts-expect-error iOS legacy
        window.navigator.standalone === true;
      setIsInstalled(standalone);
    };

    checkStandalone();

    const mql = window.matchMedia('(display-mode: standalone)');
    mql.addEventListener('change', checkStandalone);
    return () => mql.removeEventListener('change', checkStandalone);
  }, []);

  // ── Cattura beforeinstallprompt ──
  useEffect(() => {
    const handler = (e: Event) => {
      // Previeni il mini-info bar nativo di Chrome (vogliamo il nostro bottone)
      e.preventDefault();
      deferredPromptRef.current = e as BeforeInstallPromptEvent;
      setIsInstallable(true);
      console.info('[PWA] beforeinstallprompt captured — app is installable');
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  // ── Rileva installazione completata ──
  useEffect(() => {
    const handler = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      deferredPromptRef.current = null;
      console.info('[PWA] App installed via appinstalled event');
    };

    window.addEventListener('appinstalled', handler);
    return () => window.removeEventListener('appinstalled', handler);
  }, []);

  const promptInstall = useCallback(async (): Promise<boolean> => {
    const deferredPrompt = deferredPromptRef.current;

    if (!deferredPrompt) {
      console.warn('[PWA] promptInstall called but deferredPrompt is null');
      return false;
    }

    setIsPrompting(true);

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      // Sia accepted che dismissed: pulisci il ref
      deferredPromptRef.current = null;
      setIsInstallable(false);

      if (outcome === 'accepted') {
        console.info('[PWA] User accepted installation');
        return true;
      }

      console.info('[PWA] User dismissed installation');
      return false;
    } catch (err) {
      console.error('[PWA] Error during install prompt:', err);
      return false;
    } finally {
      setIsPrompting(false);
    }
  }, []);

  return {
    isInstallable,
    isInstalled,
    isPrompting,
    promptInstall,
  };
}
